import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { defaultPageContent } from '../contexts/DefaultPageContent';
import { INDUSTRY_LANDING_CONFIGS } from '../contexts/industryPagesData';

export const IndustriesIndexPage: React.FC = () => {
    const { content } = useContent();
    const pageContent = content.industriesIndex || defaultPageContent.industriesIndex;

    const onSeoApplied = useCallback(() => {
        window.dispatchEvent(new Event('app-rendered'));
    }, []);

    useSeo(pageContent, 'industriesIndex', onSeoApplied);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');

    const categories = useMemo(() => {
        const unique = Array.from(new Set(INDUSTRY_LANDING_CONFIGS.map(c => c.category)));
        return ['All', ...unique];
    }, []);

    const filteredIndustries = useMemo(() => {
        return INDUSTRY_LANDING_CONFIGS.filter(item => {
            const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
            const q = searchQuery.trim().toLowerCase();
            if (!q) return matchesCategory;
            const industryLabel = (item.defaultIndustryInput || item.h1 || '').toLowerCase();
            const targetKw = (item.targetKeyword || '').toLowerCase();
            const cat = (item.category || '').toLowerCase();
            const keywords = (item.defaultKeywordsInput || '').toLowerCase();
            const subtitle = (item.subtitle || '').toLowerCase();
            return (
                matchesCategory &&
                (industryLabel.includes(q) ||
                    targetKw.includes(q) ||
                    cat.includes(q) ||
                    keywords.includes(q) ||
                    subtitle.includes(q))
            );
        });
    }, [searchQuery, selectedCategory]);

    const groupedIndustries = useMemo(() => {
        const groups: Record<string, typeof INDUSTRY_LANDING_CONFIGS> = {};
        filteredIndustries.forEach(item => {
            if (!groups[item.category]) {
                groups[item.category] = [];
            }
            groups[item.category].push(item);
        });
        return groups;
    }, [filteredIndustries]);

    return (
        <div className="bg-slate-50 text-slate-800">
            {/* Hero Header */}
            <section className="relative py-16 md:py-24 bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-900 text-white overflow-hidden">
                <div className="container mx-auto px-4 max-w-6xl relative z-10 text-center">
                    <span className="inline-block px-3.5 py-1.5 rounded-md bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-4">
                        30 Specialized Linguistic Engines
                    </span>
                    <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
                        Industry Brand Name Generators
                    </h1>
                    <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Every industry operates under distinct phonetic rules, trademark densities, and consumer psychology. Select your vertical below to launch a tailored brand name generator calibrated with niche root words, naming frameworks, and instant domain checks.
                    </p>

                    {/* Search & Filter Bar */}
                    <div className="max-w-2xl mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by industry or keyword (e.g., Luxury, Skincare, Car, Streetwear, Coffee)..."
                                className="w-full px-5 py-4 pl-12 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-base shadow-lg"
                            />
                            <svg
                                className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    {/* Category Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-2 mt-6">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    selectedCategory === category
                                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Directory Grid */}
            <section className="py-16 container mx-auto px-4 max-w-7xl">
                {Object.keys(groupedIndustries).length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 p-8">
                        <h2 className="text-2xl font-bold mb-2">No matching industry found</h2>
                        <p className="text-slate-600 mb-6">
                            Try clearing your search filter or use our universal Brand Name Generator on the homepage.
                        </p>
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedCategory('All');
                            }}
                            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
                        >
                            Reset Filters
                        </button>
                    </div>
                ) : (
                    <div className="space-y-16">
                        {Object.entries(groupedIndustries).map(([categoryName, items]) => (
                            <div key={categoryName}>
                                <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-8">
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                                            {categoryName}
                                        </h2>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Specialized linguistic generators & naming guides for {categoryName.toLowerCase()}
                                        </p>
                                    </div>
                                    <span className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded bg-slate-200 text-slate-700">
                                        {items.length} {items.length === 1 ? 'Generator' : 'Generators'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {items.map(cfg => {
                                        const normalizedPath = cfg.slug.startsWith('/') ? cfg.slug : `/${cfg.slug}/`;
                                        const keywordTags = (cfg.defaultKeywordsInput || '')
                                            .split(',')
                                            .map(kw => kw.trim())
                                            .filter(Boolean)
                                            .slice(0, 4);
                                        return (
                                            <Link
                                                key={cfg.key}
                                                to={normalizedPath}
                                                className="group bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-indigo-500 shadow-sm hover:shadow-xl transition-all duration-200 flex flex-col justify-between"
                                            >
                                                <div>
                                                    <div className="flex items-center justify-between gap-2 mb-3">
                                                        <span className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                                                            {cfg.defaultStyle} Style
                                                        </span>
                                                        <span className="text-xs text-slate-400 font-mono">
                                                            {normalizedPath}
                                                        </span>
                                                    </div>
                                                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2">
                                                        {cfg.h1}
                                                    </h3>
                                                    <p className="text-sm text-slate-600 line-clamp-3 mb-4 leading-relaxed">
                                                        {cfg.subtitle}
                                                    </p>
                                                </div>

                                                <div className="pt-4 border-t border-slate-100 mt-2">
                                                    <div className="flex flex-wrap gap-1.5 mb-4">
                                                        {keywordTags.map((kw, i) => (
                                                            <span
                                                                key={i}
                                                                className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600"
                                                            >
                                                                {kw}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center justify-between text-xs font-semibold text-indigo-600">
                                                        <span>Launch {cfg.defaultIndustryInput} Generator</span>
                                                        <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Cross-Link to Brand Audit & Social Media Audit */}
            <section className="py-16 bg-white border-t border-slate-200">
                <div className="container mx-auto px-4 max-w-5xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                                Step 2: Validate Your Shortlist
                            </span>
                            <h3 className="text-2xl font-bold mt-2 mb-3 text-slate-900">
                                Deterministic Brand Name Audit
                            </h3>
                            <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                                Already generated a shortlist of names? Run them through our proprietary linguistic, phonetic, memorability, and trademark risk scoring engine before registering your domain.
                            </p>
                            <Link
                                to="/brand-audit/"
                                className="inline-flex items-center px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
                            >
                                Run Brand Name Audit →
                            </Link>
                        </div>

                        <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                                Step 3: Optimize Social Presence
                            </span>
                            <h3 className="text-2xl font-bold mt-2 mb-3 text-slate-900">
                                Instagram & Social Media Brand Audit
                            </h3>
                            <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                                Evaluate how effectively your brand presents itself on Instagram. Get a deterministic 100-point audit across handle hygiene, bio conversion architecture, content mix, and engagement trust.
                            </p>
                            <Link
                                to="/social-media-audit/"
                                className="inline-flex items-center px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                            >
                                Launch Social Media Audit →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
